/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DirectivesTable - Gestor de Templates de Mensajes
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file DirectivesTable.tsx
 * @version 1.0.1
 * @description Componente para crear, editar y gestionar flujos dinámicos
 * @created 2025-01-27 - Issue #1 Templates System
 * @updated 2025-01-28 - Apple Aqua Modal Style + BringToFront System
 * 
 * FEATURES:
 * - ➕ Gestión de Flujos (con UserID)
 */

import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Spinner, Alert, Modal, Form, Badge, Card, Row, Col, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrashAlt, faPlus, faSync, faStream, faInfoCircle, faGripVertical, faChartBar, faRobot, faClock, faUser, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { getHatateToken } from '../helpers/utils';
import { UserContext } from './UserContext';

interface Flow {
  id?: string;
  keywords: string[];
  hasResponse: boolean;
  responseType: string;
  response: string;
  createdAt?: string;
  updatedAt?: string;
  readOnly: boolean;
  source?: string;
  usageStats?: {
    totalTriggers: number;
    lastTriggered: string;
    avgResponseTime: number;
  };
}

interface DirectivesTableProps {
  language: 'es' | 'en';
}

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
const API_KEY = 'anubis-whatsapp-api-key';

const DirectivesTable: React.FC<DirectivesTableProps> = ({ language }) => {
  const [initialLoading, setInitialLoading] = useState(true); // Solo primera carga
  const [silentRefresh, setSilentRefresh] = useState(false);  // Refrescos en background
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [currentFlow, setCurrentFlow] = useState<Flow | null>(null);
  const [flowToDelete, setFlowToDelete] = useState<Flow | null>(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [responseInput, setResponseInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isGodUser, setIsGodUser] = useState(false);

  const userContext = useContext(UserContext);
  const user = userContext?.user;

  const API_TOKEN = getHatateToken();

  useEffect(() => {
    loadFlows();
  }, []);

useEffect(() => {
  loadFlows(false);
  const interval = setInterval(() => {
    loadFlows(true);
  }, 5000); // 5 segundos
  
  return () => clearInterval(interval);
}, [user?._id]);

const loadFlows = async (silent = false) => {
  if (silent) {
    setSilentRefresh(true);  // ✅ Refresh silencioso
  } else {
    setInitialLoading(true);  // ✅ Loading completo
  }
  
  setError(null);
  
  try {
    const headers: any = {
      'x-api-key': API_KEY,
      'Authorization': `${API_TOKEN}`
    };
    
    if (user?._id) {
      headers['X-User-ID'] = user._id;
    }

    const response = await axios.get(`${API_BASE_URL}/flows`, {
      headers
    });
    
    if (response.data.success) {
      setFlows(response.data.flows || []);
      setIsGodUser(response.data.userIsGod);
    }
  } catch (err: any) {
    console.error('[DirectivesTable] Error loading flows:', err);
    // Solo mostrar error si NO es refresh silencioso
    if (!silent) {
      setError(err.response?.data?.message || err.message);
    }
  } finally {
    if (silent) {
      setSilentRefresh(false);
    } else {
      setInitialLoading(false);
    }
  }
};
/*
  const handleReload = async () => {
    setIsSubmitting(true);
    try {
      const headers: any = {
        'x-api-key': API_KEY,
        'Authorization': `${API_TOKEN}`
      };

      if (user?._id) {
        headers['X-User-ID'] = user._id;
      }

      await axios.post(`${API_BASE_URL}/flows/reload`, {}, { headers });
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };*/

  const handleAddFlow = () => {
    setModalMode('add');
    setKeywordInput('');
    setResponseInput('');
    setCurrentFlow(null);
    setShowModal(true);
    setError(null);
  };

  const handleEditFlow = (flow: Flow) => {
    if (flow.readOnly) {
      setError(language === 'es' 
        ? 'No se pueden editar flujos del sistema' 
        : 'System flows cannot be edited');
      return;
    }
    
    setModalMode('edit');
    setKeywordInput(flow.keywords.join(', '));
    setResponseInput(flow.response);
    setCurrentFlow(flow);
    setShowModal(true);
    setError(null);
  };

  // ✅ NUEVO: Mostrar modal de confirmación personalizado
  const handleDeleteClick = (flow: Flow) => {
    if (flow.readOnly) {
      setError(language === 'es' 
        ? 'No se pueden eliminar flujos del sistema' 
        : 'System flows cannot be deleted');
      return;
    }

    setFlowToDelete(flow);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!flowToDelete?.id) return;
    
    setIsSubmitting(true);
    try {
      const headers: any = {
        'x-api-key': API_KEY,
        'Authorization': `${API_TOKEN}`
      };

      if (user?._id) {
        headers['X-User-ID'] = user._id;
      }

      await axios.delete(`${API_BASE_URL}/flows/delete/${flowToDelete.id}`, { headers });
      setShowDeleteModal(false);
      setFlowToDelete(null);
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setFlowToDelete(null);
  };

  const handleSubmit = async () => {
    if (!keywordInput.trim() || !responseInput.trim()) {
      setError(language === 'es' 
        ? 'Los lanzadores y la respuesta son obligatorios' 
        : 'Triggers and response are required');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    const keywords = keywordInput
      .split(',')
      .map(kw => kw.trim())
      .filter(kw => kw.length > 0);
    
    if (keywords.length === 0) {
      setError(language === 'es'
        ? 'Debe especificar al menos un lanzador'
        : 'You must specify at least one trigger');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const headers: any = {
        'x-api-key': API_KEY,
        'Authorization': `${API_TOKEN}`
      };

      if (user?._id) {
        headers['X-User-ID'] = user._id;
      }

      if (modalMode === 'add') {
        await axios.post(`${API_BASE_URL}/flows/create`, 
          { keywords, response: responseInput.trim() },
          { headers }
        );
      } else if (currentFlow && currentFlow.id) {
        await axios.put(`${API_BASE_URL}/flows/update/${currentFlow.id}`, 
          { keywords, response: responseInput.trim() },
          { headers }
        );
      }
      
      setShowModal(false);
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === index) return;

    const newFlows = [...flows];
    const draggedItem = newFlows[draggedIndex];
    
    newFlows.splice(draggedIndex, 1);
    newFlows.splice(index, 0, draggedItem);
    
    setFlows(newFlows);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const formatLastTriggered = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return language === 'es' ? 'Ahora' : 'Now';
    if (diffMins < 60) return `${diffMins} min`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  };

  const totalTriggers = flows.reduce((sum, flow) => sum + (flow.usageStats?.totalTriggers || 0), 0);
  const activeFlows = flows.filter(f => f.hasResponse).length;
  const avgResponseTime = flows.length > 0 
    ? Math.round(flows.reduce((sum, flow) => sum + (flow.usageStats?.avgResponseTime || 0), 0) / flows.length)
    : 0;
  const maxTriggers = Math.max(...flows.map(f => f.usageStats?.totalTriggers || 0), 1);
  
  const topFlows = [...flows]
    .sort((a, b) => (b.usageStats?.totalTriggers || 0) - (a.usageStats?.totalTriggers || 0))
    .slice(0, 5);

  if (initialLoading) {  // ✅ Solo mostrar en carga inicial
  return (
    <div className="text-center py-5">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3 text-muted">
        {language === 'es' ? 'Cargando flujos...' : 'Loading flows...'}
      </p>
    </div>
  );
}

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {isGodUser && flows.some(f => f.readOnly) && (
        <Alert variant="info" className="mb-3">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          <strong>
            {language === 'es' ? 'Modo Administrador:' : 'Admin Mode:'}
          </strong>{' '}
          {language === 'es' 
            ? 'Puedes ver flujos del sistema (marcados como "Sistema"). Estos flujos no son editables.' 
            : 'You can see system flows (marked as "System"). These flows are not editable.'}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={4}>
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Card.Body style={{ padding: '20px' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '4px' }}>
                    {language === 'es' ? 'Directivas Configuradas' : 'Configured Directives'}
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--bs-primary)' }}>
                    {flows.length}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)', marginTop: '2px' }}>
                    {activeFlows} {language === 'es' ? 'activas' : 'active'}
                  </div>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(0,123,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FontAwesomeIcon icon={faStream} style={{ fontSize: '1.5rem', color: '#007bff' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Card.Body style={{ padding: '20px' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '4px' }}>
                    {language === 'es' ? 'Total Activaciones' : 'Total Triggers'}
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#28a745' }}>
                    {totalTriggers.toLocaleString()}
                  </div>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(40,167,69,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FontAwesomeIcon icon={faRobot} style={{ fontSize: '1.5rem', color: '#28a745' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Card.Body style={{ padding: '20px' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '4px' }}>
                    {language === 'es' ? 'Tiempo Respuesta Prom.' : 'Avg Response Time'}
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#ffc107' }}>
                    {avgResponseTime}ms
                  </div>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255,193,7,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FontAwesomeIcon icon={faClock} style={{ fontSize: '1.5rem', color: '#ffc107' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Card.Header style={{ 
  backgroundColor: 'var(--bs-body-bg)', 
  borderBottom: '1px solid var(--bs-border-color)', 
  padding: '16px 20px' 
}}>
  <div className="d-flex justify-content-between align-items-center">
    <div className="d-flex align-items-center">
      <h5 className="mb-0" style={{ fontWeight: 600, color: 'var(--bs-body-color)' }}>
        <FontAwesomeIcon icon={faStream} className="me-2" style={{ color: '#007bff' }} />
        {language === 'es' ? 'Directivas de Respuesta Automática' : 'Auto Answer Directives'}
      </h5>
      {/* ✅ AGREGAR BADGE DE ESTADO EN VIVO */}
      <Badge 
  bg={silentRefresh ? "warning" : "success"}  // ✅ Amarillo al refrescar
  className="ms-3"
  style={{ 
    fontSize: '0.7rem',
    padding: '4px 8px',
    fontWeight: 500,
    animation: silentRefresh ? 'pulse 0.5s ease-in-out' : 'pulse 2s infinite',
    transition: 'all 0.3s ease'
  }}
>
  <span style={{ 
    display: 'inline-block', 
    width: '6px', 
    height: '6px', 
    borderRadius: '50%', 
    backgroundColor: 'white', 
    marginRight: '6px' 
  }}></span>
  {silentRefresh 
    ? (language === 'es' ? 'Sincronizando...' : 'Syncing...') 
    : (language === 'es' ? 'En Vivo' : 'Live')
  }
</Badge>
    </div>
    <div>
      {/* El botón Reload ya no existe aquí */}
      <Button 
        variant="primary" 
        size="sm"
        onClick={handleAddFlow}
        style={{ borderRadius: '8px' }}
      >
        <FontAwesomeIcon icon={faPlus} className="me-2" />
        {language === 'es' ? 'Nueva Directiva' : 'New Directive'}
      </Button>
    </div>
  </div>
</Card.Header>
        
        <Card.Body style={{ padding: '24px' }}>
          {error && (
            <Alert 
              variant="danger" 
              dismissible 
              onClose={() => setError(null)}
              style={{ borderRadius: '8px', marginBottom: '20px' }}
            >
              {error}
            </Alert>
          )}
          
          {flows.length === 0 ? (
            <Alert 
              variant="info"
              style={{ 
                borderRadius: '8px',
                backgroundColor: 'rgba(0,123,255,0.1)',
                border: '1px solid rgba(0,123,255,0.2)',
                padding: '16px'
              }}
            >
              <div className="d-flex align-items-start">
                <FontAwesomeIcon icon={faInfoCircle} style={{ marginTop: '2px', marginRight: '12px', color: '#007bff' }} />
                <div>
                  <span style={{ fontSize: '0.9rem', color: 'var(--bs-secondary)' }}>
                    {language === 'es' 
                      ? 'No hay directivas de respuesta configuradas. Agregue una nueva directiva para automatizar respuestas.' 
                      : 'No response directives configured. Add a new directive to automate responses.'}
                  </span>
                </div>
              </div>
            </Alert>
          ) : (
            <Table responsive hover style={{ marginBottom: 0 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--bs-border-color)' }}>
                  <th style={{ padding: '12px', fontSize: '0.85rem', color: 'var(--bs-secondary)', fontWeight: 600, width: '30px' }}></th>
                  <th style={{ padding: '12px', fontSize: '0.85rem', color: 'var(--bs-secondary)', fontWeight: 600 }}>
                    {language === 'es' ? 'Lanzadores' : 'Triggers'}
                  </th>
                  <th style={{ padding: '12px', fontSize: '0.85rem', color: 'var(--bs-secondary)', fontWeight: 600 }}>
                    {language === 'es' ? 'Respuesta' : 'Response'}
                  </th>
                  <th className="text-center" style={{ padding: '12px', fontSize: '0.85rem', color: 'var(--bs-secondary)', fontWeight: 600, width: '100px' }}>
                    Estado
                  </th>
                  <th style={{ padding: '12px', fontSize: '0.85rem', color: 'var(--bs-secondary)', fontWeight: 600 }}>
                    {language === 'es' ? 'Activaciones' : 'Triggers'}
                  </th>
                  <th style={{ padding: '12px', fontSize: '0.85rem', color: 'var(--bs-secondary)', fontWeight: 600 }}>
                    {language === 'es' ? 'Últ. Uso' : 'Last Used'}
                  </th>
                  <th className="text-center" style={{ padding: '12px', fontSize: '0.85rem', color: 'var(--bs-secondary)', fontWeight: 600, width: '140px' }}>
                    {language === 'es' ? 'Acciones' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {flows.map((flow, idx) => (
                  <tr 
                    key={flow.id || idx} 
                    draggable={!flow.readOnly}
                    onDragStart={() => !flow.readOnly && handleDragStart(idx)}
                    onDragOver={(e) => !flow.readOnly && handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    style={{ 
                      borderBottom: '1px solid var(--bs-border-color)',
                      cursor: flow.readOnly ? 'default' : 'move',
                      opacity: draggedIndex === idx ? 0.5 : 1,
                      backgroundColor: draggedIndex === idx ? 'var(--bs-secondary-bg)' : 'transparent'
                    }}
                  >
                    <td style={{ padding: '16px 12px', verticalAlign: 'middle' }}>
                      {!flow.readOnly && (
                        <FontAwesomeIcon icon={faGripVertical} style={{ color: 'var(--bs-secondary)', cursor: 'grab' }} />
                      )}
                    </td>
                    <td style={{ padding: '16px 12px', verticalAlign: 'middle' }}>
                      <div className="d-flex flex-wrap gap-1">
                        {flow.keywords.map((kw, i) => (
                          <Badge 
                            key={i} 
                            bg="secondary" 
                            style={{ 
                              fontSize: '0.8rem',
                              padding: '4px 8px',
                              fontWeight: 500
                            }}
                          >
                            {kw}
                          </Badge>
                        ))}
                        {flow.readOnly && (
                          <Badge bg="info" style={{ fontSize: '0.7rem', marginLeft: '4px' }}>
                            {language === 'es' ? 'Sistema' : 'System'}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px', verticalAlign: 'middle' }}>
                      <div style={{ 
                        maxWidth: '300px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        fontSize: '0.9rem',
                        color: 'var(--bs-body-color)'
                      }}>
                        {flow.response || (
                          <span style={{ color: 'var(--bs-secondary)', fontStyle: 'italic' }}>
                            {language === 'es' ? 'Sin respuesta' : 'No response'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center" style={{ padding: '16px 12px', verticalAlign: 'middle' }}>
                      <Badge bg={flow.hasResponse ? 'success' : 'warning'}>
                        {flow.hasResponse 
                          ? (language === 'es' ? 'Activa' : 'Active') 
                          : (language === 'es' ? 'Incompleta' : 'Incomplete')}
                      </Badge>
                    </td>
                    <td style={{ padding: '16px 12px', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--bs-body-color)' }}>
                        {flow.usageStats?.totalTriggers?.toLocaleString() || '0'}
                      </div>
                      <ProgressBar 
                        now={maxTriggers > 0 ? ((flow.usageStats?.totalTriggers || 0) / maxTriggers) * 100 : 0} 
                        variant="primary"
                        style={{ height: '4px', borderRadius: '4px', marginTop: '4px' }}
                      />
                    </td>
                    <td style={{ padding: '16px 12px', verticalAlign: 'middle' }}>
                      <Badge bg="secondary">
                        {flow.usageStats?.lastTriggered ? formatLastTriggered(flow.usageStats.lastTriggered) : '-'}
                      </Badge>
                    </td>
                    <td className="text-center" style={{ padding: '16px 12px', verticalAlign: 'middle' }}>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2" 
                        onClick={() => handleEditFlow(flow)} 
                        disabled={isSubmitting || flow.readOnly} 
                        style={{ borderRadius: '6px' }}
                        title={flow.readOnly ? (language === 'es' ? 'No editable' : 'Not editable') : ''}
                      >
                        <FontAwesomeIcon icon={faPencilAlt} />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={() => handleDeleteClick(flow)} 
                        disabled={isSubmitting || flow.readOnly}
                        style={{ borderRadius: '6px' }}
                        title={flow.readOnly ? (language === 'es' ? 'No eliminable' : 'Not deletable') : ''}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Alert 
            variant="info" 
            className="mt-4 mb-0"
            style={{ 
              borderRadius: '8px',
              backgroundColor: 'rgba(0,123,255,0.1)',
              border: '1px solid rgba(0,123,255,0.2)',
              padding: '16px'
            }}
          >
            <div className="d-flex align-items-start">
              <FontAwesomeIcon icon={faInfoCircle} style={{ marginTop: '2px', marginRight: '12px', color: '#007bff' }} />
              <div>
                <strong style={{ color: 'var(--bs-body-color)' }}>
                  {language === 'es' ? 'Orden de Evaluación:' : 'Evaluation Order:'}
                </strong>
                <br />
                <span style={{ fontSize: '0.9rem', color: 'var(--bs-secondary)' }}>
                  {language === 'es' 
                    ? 'Arrastra las filas para cambiar el orden de prioridad. Las directivas se evaluarán en el orden mostrado.' 
                    : 'Drag rows to change priority order. Directives will be evaluated in the order shown.'}
                </span>
              </div>
            </div>
          </Alert>
        </Card.Body>
      </Card>

      {/* ✅ NUEVO: Modal de confirmación de eliminación estilizado */}
      <Modal 
        show={showDeleteModal} 
        onHide={handleCancelDelete}
        centered
        size="sm"
      >
        <Modal.Body style={{ padding: '32px 24px', textAlign: 'center' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: 'rgba(220,53,69,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <FontAwesomeIcon 
              icon={faExclamationTriangle} 
              style={{ fontSize: '2rem', color: '#dc3545' }} 
            />
          </div>
          
          <h5 style={{ fontWeight: 600, marginBottom: '12px', color: 'var(--bs-body-color)' }}>
            {language === 'es' ? '¿Eliminar Directiva?' : 'Delete Directive?'}
          </h5>
          
          <p style={{ color: 'var(--bs-secondary)', marginBottom: '8px', fontSize: '0.95rem' }}>
            {language === 'es' 
              ? 'Esta acción no se puede deshacer. La directiva se eliminará permanentemente.'
              : 'This action cannot be undone. The directive will be permanently deleted.'}
          </p>
          
          {flowToDelete && (
            <div style={{
              backgroundColor: 'var(--bs-secondary-bg)',
              padding: '12px',
              borderRadius: '8px',
              marginTop: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '4px' }}>
                {language === 'es' ? 'Lanzadores:' : 'Triggers:'}
              </div>
              <div className="d-flex flex-wrap gap-1 justify-content-center">
                {flowToDelete.keywords.map((kw, i) => (
                  <Badge key={i} bg="secondary" style={{ fontSize: '0.75rem' }}>
                    {kw}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          <div className="d-flex gap-2 justify-content-center">
            <Button 
              variant="secondary" 
              onClick={handleCancelDelete}
              disabled={isSubmitting}
              style={{ 
                borderRadius: '8px',
                minWidth: '100px',
                fontWeight: 500
              }}
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              variant="danger" 
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
              style={{ 
                borderRadius: '8px',
                minWidth: '100px',
                fontWeight: 500
              }}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {language === 'es' ? 'Eliminando...' : 'Deleting...'}
                </>
              ) : (
                language === 'es' ? 'Sí, Eliminar' : 'Yes, Delete'
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Métricas Avanzadas */}
      <Row className="mt-4">
        <Col md={6}>
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '100%' }}>
            <Card.Header style={{ 
              backgroundColor: 'var(--bs-body-bg)', 
              borderBottom: '1px solid var(--bs-border-color)', 
              padding: '16px 20px' 
            }}>
              <h6 className="mb-0" style={{ fontWeight: 600, color: 'var(--bs-body-color)' }}>
                <FontAwesomeIcon icon={faChartBar} className="me-2" />
                {language === 'es' ? 'Métricas de Rendimiento' : 'Performance Metrics'}
              </h6>
            </Card.Header>
            <Card.Body style={{ padding: '20px' }}>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
                    {language === 'es' ? 'Tasa de Activación' : 'Activation Rate'}
                  </span>
                  <strong style={{ fontSize: '0.85rem', color: 'var(--bs-body-color)' }}>
                    {flows.length > 0 ? Math.round((activeFlows / flows.length) * 100) : 0}%
                  </strong>
                </div>
                <ProgressBar 
                  now={flows.length > 0 ? (activeFlows / flows.length) * 100 : 0}
                  variant="success"
                  style={{ height: '8px', borderRadius: '4px' }}
                />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
                    {language === 'es' ? 'Cobertura de Keywords' : 'Keyword Coverage'}
                  </span>
                  <strong style={{ fontSize: '0.85rem', color: 'var(--bs-body-color)' }}>
                    {flows.reduce((sum, f) => sum + f.keywords.length, 0)} keywords
                  </strong>
                </div>
                <ProgressBar 
                  now={Math.min((flows.reduce((sum, f) => sum + f.keywords.length, 0) / 50) * 100, 100)}
                  variant="info"
                  style={{ height: '8px', borderRadius: '4px' }}
                />
              </div>

              <div>
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
                    {language === 'es' ? 'Eficiencia de Respuesta' : 'Response Efficiency'}
                  </span>
                  <strong style={{ fontSize: '0.85rem', color: 'var(--bs-body-color)' }}>
                    {avgResponseTime < 200 ? (language === 'es' ? 'Excelente' : 'Excellent') : avgResponseTime < 300 ? (language === 'es' ? 'Buena' : 'Good') : (language === 'es' ? 'Regular' : 'Fair')}
                  </strong>
                </div>
                <ProgressBar 
                  now={avgResponseTime > 0 ? Math.max(100 - (avgResponseTime / 5), 20) : 0}
                  variant={avgResponseTime < 200 ? 'success' : avgResponseTime < 300 ? 'warning' : 'danger'}
                  style={{ height: '8px', borderRadius: '4px' }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '100%' }}>
            <Card.Header style={{ 
              backgroundColor: 'var(--bs-body-bg)', 
              borderBottom: '1px solid var(--bs-border-color)', 
              padding: '16px 20px' 
            }}>
              <h6 className="mb-0" style={{ fontWeight: 600, color: 'var(--bs-body-color)' }}>
                {language === 'es' ? 'Estadísticas Detalladas' : 'Detailed Statistics'}
              </h6>
            </Card.Header>
            <Card.Body style={{ padding: '20px' }}>
              <div className="d-flex justify-content-between mb-3 pb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)', marginBottom: '2px' }}>
                    {language === 'es' ? 'Promedio Keywords/Directiva' : 'Avg Keywords/Directive'}
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--bs-body-color)' }}>
                    {flows.length > 0 
                      ? (flows.reduce((sum, f) => sum + f.keywords.length, 0) / flows.length).toFixed(1)
                      : '0'}
                  </div>
                </div>
                <div className="text-end">
                  <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)', marginBottom: '2px' }}>
                    {language === 'es' ? 'Directiva Más Usada' : 'Most Used Directive'}
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#28a745' }}>
                    {topFlows.length > 0 ? topFlows[0].usageStats?.totalTriggers || 0 : 0}
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between mb-3 pb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)', marginBottom: '2px' }}>
                    {language === 'es' ? 'Total Keywords' : 'Total Keywords'}
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--bs-body-color)' }}>
                    {flows.reduce((sum, f) => sum + f.keywords.length, 0)}
                  </div>
                </div>
                <div className="text-end">
                  <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)', marginBottom: '2px' }}>
                    {language === 'es' ? 'Tiempo Más Rápido' : 'Fastest Time'}
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#ffc107' }}>
                    {flows.length > 0 
                      ? Math.min(...flows.map(f => f.usageStats?.avgResponseTime || Infinity))
                      : 0}ms
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between">
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)', marginBottom: '2px' }}>
                    {language === 'es' ? 'Uso Total Sistema' : 'Total System Usage'}
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--bs-body-color)' }}>
                    {totalTriggers.toLocaleString()}
                  </div>
                </div>
                <div className="text-end">
                  <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)', marginBottom: '2px' }}>
                    {language === 'es' ? 'Promedio Uso/Directiva' : 'Avg Usage/Directive'}
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#007bff' }}>
                    {flows.length > 0 ? Math.round(totalTriggers / flows.length) : 0}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para Crear/Editar */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal}
        centered
      >
        <Modal.Header 
          closeButton 
          style={{ 
            borderBottom: '1px solid var(--bs-border-color)',
            padding: '16px 20px',
            backgroundColor: 'var(--bs-body-bg)'
          }}
        >
          <Modal.Title style={{ fontWeight: 600, fontSize: '1.1rem', color: 'var(--bs-body-color)' }}>
            {modalMode === 'add' 
              ? (language === 'es' ? 'Nueva Directiva' : 'New Directive')
              : (language === 'es' ? 'Editar Directiva' : 'Edit Directive')}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '24px', backgroundColor: 'var(--bs-body-bg)' }}>
          {error && (
            <Alert variant="danger" style={{ borderRadius: '8px', marginBottom: '16px' }}>
              {error}
            </Alert>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--bs-body-color)' }}>
                {language === 'es' ? 'Lanzadores (separados por comas)' : 'Triggers (comma separated)'}
              </Form.Label>
              <Form.Control 
                type="text" 
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder={language === 'es' ? 'ej: hola, saludos, buenos días' : 'e.g: hello, hi, good morning'}
                disabled={isSubmitting}
                style={{ 
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '0.9rem'
                }}
              />
              <Form.Text style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
                {language === 'es' 
                  ? 'Palabras o frases que activarán esta respuesta automática' 
                  : 'Words or phrases that will trigger this automatic response'}
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--bs-body-color)' }}>
                {language === 'es' ? 'Respuesta' : 'Response'}
              </Form.Label>
              <Form.Control 
                as="textarea" 
                rows={5}
                value={responseInput}
                onChange={(e) => setResponseInput(e.target.value)}
                placeholder={language === 'es' 
                  ? 'Escriba la respuesta que se enviará automáticamente...' 
                  : 'Write the response that will be sent automatically...'}
                disabled={isSubmitting}
                style={{ 
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        
        <Modal.Footer style={{ borderTop: '1px solid var(--bs-border-color)', padding: '16px 20px', backgroundColor: 'var(--bs-body-bg)' }}>
          <Button 
            variant="secondary" 
            onClick={handleCloseModal} 
            disabled={isSubmitting}
            style={{ borderRadius: '8px' }}
          >
            {language === 'es' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            style={{ borderRadius: '8px' }}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {language === 'es' ? 'Guardando...' : 'Saving...'}
              </>
            ) : (
              language === 'es' ? 'Guardar' : 'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .table-hover tbody tr:hover {
          background-color: var(--bs-secondary-bg);
        }

        .btn-outline-primary, .btn-outline-danger {
          transition: all 0.2s ease;
        }

        .btn-outline-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,123,255,0.2);
        }

        .btn-outline-danger:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(220,53,69,0.2);
        }

        .btn-outline-primary:disabled, .btn-outline-danger:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .modal-content {
          border-radius: 12px !important;
          border: none;
          box-shadow: 0 4px 16px rgba(0,0,0,0.15);
        }

        .form-control:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.15);
        }

        .badge {
          transition: all 0.2s ease;
        }

        tbody tr {
          transition: opacity 0.2s ease, background-color 0.2s ease;
        }

        tbody tr:active {
          cursor: grabbing;
        }

        .btn-group .btn {
          transition: all 0.2s ease;
        }

        .progress-bar {
          transition: width 0.6s ease;
        }
        
        /* ✅ AGREGAR ANIMACIÓN DE PULSO */
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
      `}</style>
    </div>
  );
};

export default DirectivesTable;